// The installed app (docs/pwa.md): which way this browser installs it, whether it already runs installed, and the
// phone install banner's rules (from the first visit; never in the installed app or in an app's built-in browser; ✕ or
// closing the steps hides it for 30 days). Pure, plus best-effort localStorage reads and writes. The copy is in the
// locale files, under components.InstallApp. The browser's own prompt lives in utils/installPrompt.

export const INSTALL_DISMISSED_KEY = 'mt-install-dismissed-at';
export const INSTALL_DISMISS_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The one switch for the logged-out landing page (/ and /login): true shows the banner there like on every other page.
 * Set it to false to keep the landing clean; the footer badge stays.
 */
export const INSTALL_BANNER_ON_LANDING = true;

/** Up to where "a phone" goes: the width the pill becomes the desktop rail (.mt-tabbar, FloatingTabBar). */
export const INSTALL_PHONE_QUERY = '(max-width: 959px)';

/**
 * What Android's floating banner takes: 74px tall, plus the 8px over the pill or the 16px over the screen's edge when
 * there's no pill. The room goes on <html> as --install-float, and everything pinned above the tab bar, and the end
 * of every page, moves up by it, in CSS, with no render.
 */
export const INSTALL_FLOAT_VAR = '--install-float';
export const INSTALL_FLOAT_ROOM = 82;
export const INSTALL_FLOAT_ROOM_ALONE = 90;

/** A window is up: the banner waits for it to close (NextUI's dialogs, the exercise library's full-screen one). */
export const WINDOW_SELECTOR = '[role="dialog"]';
/** The iPhone's banner sits in the page, so an open workout (fixed under the header) would cut it: it waits for that too. */
export const WINDOW_OR_PHONE_PAGE_SELECTOR = `${WINDOW_SELECTOR}, .mt-tr-phone-page`;

// Instagram, Facebook / Messenger, TikTok, LINE, Snapchat, Pinterest, LinkedIn, X: their own web views.
const IN_APP = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|FB4A|Messenger|musical_ly|Bytedance|TikTok|\bLine\/|Snapchat|Pinterest|LinkedInApp|Twitter for/i;
const CRAWLER = /googlebot|google-inspectiontool|googleother|bingbot|applebot|yandexbot|duckduckbot|baiduspider|ahrefsbot|semrushbot|petalbot|gptbot|claudebot|facebookexternalhit|meta-externalagent/i;

export const isInAppBrowser = (userAgent) => IN_APP.test(userAgent);
export const isCrawler = (userAgent) => CRAWLER.test(userAgent);
export const isAndroid = (userAgent) => /Android/i.test(userAgent);

/** iPhone, iPod or iPad (iPadOS says "Macintosh", but a Mac has no touch screen). */
export const isIos = (userAgent, maxTouchPoints = 0) =>
  /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

/**
 * The browser announces the install prompt (`onbeforeinstallprompt`: Chrome, Edge, Samsung Internet; never Safari, nor
 * Chrome / Edge on an iPhone, which run on Safari's engine). Such a browser installs only through that prompt, whatever
 * its user agent says.
 */
export const promptCapable = (win = typeof window === 'undefined' ? undefined : window) => win != null && 'onbeforeinstallprompt' in win;

/**
 * How this browser installs the app:
 * - `prompt`: it offered its install prompt.
 * - `android`: Android before that prompt comes. Chrome sends it only after a tap and some seconds, so the banner
 *   doesn't wait: "Instalar" opens the steps through the browser's menu until it's in.
 * - `ios`: iPhone / iPad (Compartilhar → Adicionar à Tela de Início).
 * - `in-app`: an app's built-in browser, where nothing can be installed.
 * - `none`: no way we know. A browser that announces the prompt stays here until it comes, so desktop Chrome posing as an
 *   iPhone never flashes Apple's banner first.
 */
export const installPlatformOf = (userAgent, hasPrompt, maxTouchPoints = 0, canPrompt = false) => {
  if (isInAppBrowser(userAgent)) return 'in-app';
  if (hasPrompt) return 'prompt';
  if (isAndroid(userAgent)) return 'android';
  if (canPrompt) return 'none';
  if (isIos(userAgent, maxTouchPoints)) return 'ios';
  return 'none';
};

/** The count's bucket of a platform (never the user agent). */
export const platformLabel = (platform, userAgent) => {
  if (platform === 'ios') return 'ios';
  if (isAndroid(userAgent)) return 'android';
  if (platform === 'prompt') return 'desktop';
  return 'other';
};

/** Running as the installed app (its own window, no browser bar): Android / desktop by display mode, iOS by navigator.standalone. */
export const isStandalone = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  if (win.navigator?.standalone === true) return true;
  const query = (mode) => {
    try {
      return win.matchMedia?.(mode).matches === true;
    } catch {
      return false;
    }
  };
  return query('(display-mode: standalone)') || query('(display-mode: fullscreen)') || query('(display-mode: minimal-ui)');
};

/**
 * The installed app on a phone (owner decision 30/10/2026): its own home, login and header, and no footer. Under the
 * rail's 960px, so a browser and a computer's installed window keep the site as it is.
 */
export const isAppOnPhone = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win || !isStandalone(win)) return false;
  try {
    return win.matchMedia?.('(max-width: 959.98px)').matches === true;
  } catch {
    return false;
  }
};

// The tab bar's own pages: everything else is an inner page, with a back button inside the installed app
const TAB_ROOTS = new Set(['/', '/workouts', '/exercises', '/diet', '/calorie-tracker']);
export const isTabRoot = (pathname) => TAB_ROOTS.has(pathname.replace(/\/+$/, '') || '/');

/** The site this page belongs to, as src/utils/i18n.js decides it. */
export const siteLocale = (hostname = window.location.hostname) =>
  ['meutreino.fit', 'www.meutreino.fit', 'localhost'].includes(hostname) ? 'pt' : 'en';

const storageOf = () => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const readInstallDismissedAt = (storage = storageOf()) => {
  try {
    const value = Number(storage?.getItem(INSTALL_DISMISSED_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

export const writeInstallDismissed = (now = Date.now(), storage = storageOf()) => {
  try {
    storage?.setItem(INSTALL_DISMISSED_KEY, String(now));
  } catch {
    // Not remembered: the banner may come back next visit
  }
};

export const dismissedRecently = (dismissedAt, now = Date.now()) => dismissedAt != null && now - dismissedAt < INSTALL_DISMISS_DAYS * DAY_MS;

/** Pages where the banner never shows: the guided session, the e-mail link, and the landing if it's switched off. */
export const installBannerHiddenOn = (pathname, onLanding = INSTALL_BANNER_ON_LANDING) => {
  if (/^\/(execute|reset-password)(\/|$)/.test(pathname)) return true;
  return !onLanding && /^\/(login(\/|$)|$)/.test(pathname);
};

/** This browser can install it and it isn't running installed (the footer badge, the line in Conta). */
export const canOfferInstall = (platform, standalone) =>
  !standalone && (platform === 'prompt' || platform === 'ios' || platform === 'android');

export const showsInstallBanner = ({ platform, standalone, phone, dismissedAt, pathname, busy, now = Date.now() }) => {
  if (!phone || busy || !canOfferInstall(platform, standalone)) return false;
  if (dismissedRecently(dismissedAt, now)) return false;
  return !installBannerHiddenOn(pathname);
};

/**
 * The banner wears each phone's own install look: Apple's Smart App Banner on an iPhone, Chrome's install banner
 * elsewhere. The iPhone's sits at the very top of the page, Android's fixed just above the tab bar, where Chrome shows its
 * own, so it's on screen however far the page is scrolled.
 */
export const installBannerLook = (platform) => (platform === 'ios' ? 'ios' : 'android');
export const installBannerPlacement = (look) => (look === 'ios' ? 'top' : 'bottom');
